/**
 * Where a player's filter lives between sessions, and how text becomes a filter.
 *
 * Persistence follows `state/settings.ts` exactly: the same injected `SettingsStore`
 * interface, the same one-key-in-localStorage shape, the same refusal to let a storage
 * failure interrupt the game. A separate key rather than a field inside the settings
 * blob, because a filter is kilobytes of text a player edits and the settings are five
 * scalars a slider writes — one bad write to a shared blob would cost both.
 *
 * ## Compiling never throws at the caller
 *
 * `parseFilter` throws on the first problem, which is right for a loader. But a HUD that
 * threw would lose the session over a typo, so `compileFilter` catches the whole thing
 * and returns the **last good filter** alongside the diagnostics. That is the client-side
 * shape of the server's fail-fast rule: the bad text is reported precisely, with a line,
 * a column and a caret, and the game keeps running on the last thing that made sense.
 * Silently running a *partly* parsed filter is the one option that is not on the table —
 * `diagnoseFilter` deliberately does not hand back the rules it did manage to read.
 */

import type { SettingsStore } from '../../state/settings';
import {
  diagnoseFilter,
  EMPTY_FILTER,
  parseFilter,
  type CompiledFilter,
  type FilterDiagnostic,
} from './parse';

const STORAGE_KEY = 'grind.lootfilter';

/**
 * The filter a player who has never written one gets.
 *
 * **Every rule is `Show`.** Wiring the filter must not take anything off the screen that
 * was there before it — a starter filter that hid Normal drops would look exactly like a
 * bug to someone who never opened the editor. So this one only ever *adds*: it labels all
 * four rarities, gives the two worth stopping for a tier that lifts their beam and rings
 * a chime, and leaves everything else where it was.
 *
 * The colour triples are the `--rarity-*` tokens from `styles/tokens.css` in decimal, so
 * a label matches the colour that rarity has everywhere else in the interface. The Tier 1
 * border is the spec's "glowing red border", which is `PlayLightBeam Red`'s own `#ff3b30`.
 *
 * Note what is *not* here: an explicit `AutoPickup`. Rares and uniques are swept by `F`
 * because they are Tier 2 and Tier 1, which is the spec's own rule — writing it out as
 * well would hide the fact that the tier is doing the work, and a player editing this
 * would then not learn that changing a tier changes what the vacuum takes.
 */
export const DEFAULT_FILTER_TEXT = `# ==========================================================
# G.R.I.N.D. starter filter
#
# Two rules, both of them Show: this file only ever makes a
# drop louder, never quieter. Edit freely — a Hide rule is
# how you quieten one, and nothing here is sacred.
#
# Magic and Normal are deliberately absent. Anything no rule
# names falls through to the built-in default, which shows
# Rare and better: so a white item lies on the floor as a
# small spinning object with no beam and no label, and you
# read it by holding Alt. Add a Show rule for Rarity ==
# MAGIC to put its label and beam back.
#
# Tier 1 and Tier 2 are what the F vacuum sweeps.
# ==========================================================

# Uniques. Always worth crossing a room for.
Show
    Rarity == UNIQUE
    SetTier 1
    SetTextColor 184 147 63
    SetBorderColor 255 59 48
    SetFontSize 18
    PlayAlertSound 1
    PlayLightBeam Red

# Rares. Worth a look, and worth the vacuum.
Show
    Rarity == RARE
    SetTier 2
    SetTextColor 212 201 74
    PlayAlertSound 2
    PlayLightBeam Yellow
`;

/**
 * The saved filter text, or the starter filter.
 *
 * **`null` and `''` are different answers**, and reading this with a falsiness test is a
 * real bug: a player who selects everything in the editor and deletes it has deliberately
 * asked for no rules, and resurrecting the starter filter on their next boot would make
 * "clear the filter" impossible. `getItem` returns null only when the key was never
 * written.
 */
export function loadFilterText(storage: SettingsStore | null): string {
  if (!storage) return DEFAULT_FILTER_TEXT;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    return raw === null ? DEFAULT_FILTER_TEXT : raw;
  } catch {
    return DEFAULT_FILTER_TEXT;
  }
}

/** Persists the filter text, ignoring a storage that refuses to write. */
export function saveFilterText(storage: SettingsStore | null, text: string): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, text);
  } catch {
    // Private browsing, a full quota, a disabled store. None of it is worth
    // interrupting a run for — the same call the settings make.
  }
}

/** A compile attempt: what to run, and everything wrong with what was asked for. */
export interface FilterCompileResult {
  /** The new filter, or `lastGood` unchanged when the text did not parse. */
  filter: CompiledFilter;
  /** Empty when the text parsed. Every problem at once, not just the first. */
  diagnostics: readonly FilterDiagnostic[];
  /** True when `filter` is the new text's. */
  ok: boolean;
}

/**
 * Compiles filter text, keeping `lastGood` if it does not parse.
 *
 * `diagnoseFilter` is asked first because it reports *every* bad line, which is what an
 * editor needs — `parseFilter` stops at the first. When it comes back clean, `parseFilter`
 * cannot then fail; the try/catch is there because "cannot" is a claim about two functions
 * agreeing, and a session should not end if they ever stop.
 */
export function compileFilter(
  text: string,
  lastGood: CompiledFilter = EMPTY_FILTER,
): FilterCompileResult {
  const diagnostics = diagnoseFilter(text);
  if (diagnostics.length > 0) return { filter: lastGood, diagnostics, ok: false };

  try {
    return { filter: parseFilter(text), diagnostics: [], ok: true };
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    return {
      filter: lastGood,
      diagnostics: [{ line: 1, column: 1, message: detail, source: '' }],
      ok: false,
    };
  }
}
